import React, {useCallback, useEffect, useState} from 'react'
import {Redirect, useLocation} from 'react-router-dom'
import {useAsync} from 'lib/async'
import {Client} from 'lib/ui/api-client'
import {api, createRoutes} from 'lib/url'
import {teamMemberClient} from 'obvio/obvio-client'
import {obvioRoutes} from 'obvio/Routes'
import {Organization} from 'organization'

export type OrganizationRoutes = ReturnType<typeof createRoutesFor>

interface OrganizationContextProps {
  organization: Organization
  routes: OrganizationRoutes
  client: Client
  set: (organization: Organization) => void
}

export const OrganizationContext = React.createContext<
  OrganizationContextProps | undefined
>(undefined)

export default function OrganizationProvider(props: {
  children: React.ReactNode
}) {
  const id = useRouteOrganizationId()

  const find = useCallback(() => {
    return findOrganization(id)
  }, [id])

  const [organization, setOrganization] = useState<Organization | null>(null)
  const {data: fetched, loading} = useAsync(find)

  useEffect(() => {
    setOrganization(fetched)
  }, [fetched])

  // If invalid id let's just redirect back to organizations list
  if (!id) {
    return <Redirect to={obvioRoutes.organizations.root} />
  }

  if (loading) {
    return null
  }

  // Did we switch organizations via history.push()? If we did, we need to wait
  // until the new organization is fetched, otherwise it will create the
  // routes for the old organization.
  const hasSwitchedOrganization = organization && organization.id !== id
  if (hasSwitchedOrganization) {
    return null
  }

  if (!organization) {
    return (
      <div>
        <h1>404 - Organization '{id}' not found.</h1>
      </div>
    )
  }

  const routes = createRoutesFor(organization)

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        routes,
        client: teamMemberClient,
        set: setOrganization,
      }}
    >
      {props.children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const context = React.useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error(
      'useOrganization must be used within a OrganizationProvider',
    )
  }

  return context
}

function useRouteOrganizationId() {
  const location = useLocation()
  // Parse id out of URL. example location: '/organization/35',
  // which would split to ['','organization','35'].
  const id = location.pathname.split('/')[2]

  try {
    return parseInt(id)
  } catch {
    return null
  }
}

export function createRoutesFor(organization: Organization) {
  return createRoutes(
    {
      login: '/login',
      forgot_password: '/forgot_password',
      reset_password: '/reset_password',
      team: '/team',
      emails: {
        verify: {
          ':token': {},
        },
      },
      settings: '/settings',
      form_submissions: {
        ':file': {},
      },
      question_submissions: {
        ':file': {},
      },
      attendees_export: {
        ':file': {},
      },
      area_attendees_export: {
        ':file': {},
      },
      room_assignments_export: {
        ':file': {},
      },
      room_attendees_export: {
        ':file': {},
      },
      waivers: {
        ':file': {},
      },
      image_entries: {
        ':file': {},
      },
      form_submissions_all: {
        ':file': {},
      },
      certificates: {
        ':file': {},
      },
      events: {
        create: '/create',
        ':event': {
          change_template: '/change_template',
          duplicate: '/duplicate',
          dashboard: '/dashboard',
          waiver: {
            additional_waivers: '/additional_waivers',
          },
          forms: {
            ':form': {
              moderation: {
                stage: {
                  ':status': {},
                },
                lower_third: {
                  ':status': {},
                },
              },
              statistics: '/statistics',
            },
          },
          emoji: {
            settings: '/settings',
          },
          redirects: '/redirects',
          speakers: '/speakers',
          sponsors: '/sponsors',
          faqs: '/faqs',
          tech_check: '/tech_check',
          attendees: '/attendees',
          points: '/points',
          localization: '/localization',
          services: {
            zapier: '/zapier',
            infusionsoft: '/infusionsoft',
            mailchimp: '/mailchimp',
            hubspot: '/hubspot',
            hubspot_v2: '/hubspot_v2',
            highlevel: '/highlevel',
            active_campaign: '/active_campaign',
            ontraport: '/ontraport',
            convert_kit: '/convert_kit',
            click_funnels: '/click_funnels',
          },
          name_appendage: '/name_appendage',
          broadcasts: '/broadcasts',
          purchase_confirmations: '/purchase_confirmations',
          backgrounds: '/zoom_backgrounds',
          areas: {
            create: '/create',
            zoom_user_settings: '/zoom_user_settings',
            ':area': {
              rules: '/rules',
              rooms: {
                create: '/create',
                ':room': {
                  recordings: '/recordings',
                  join: '/join',
                },
              },
            },
          },
          zoom_attendance: '/zoom_attendance',
          leaderboard: '/leaderboard',
          reports: '/reports',
          scripts: '/scripts',
          image_entries: '/image_entries',
          image_waterfall: {
            settings: '/settings',
          },
          login: '/login',
          forgot_password: '/forgot_password',
          change_password: '/change_password',
          sign_up: '/sign_up',
          welcome_email: '/welcome_email',
          forgot_password_email: '/forgot_password_email',
          event_offline: '/event_offline',
          password_create: '/password_create',
          global_styles: '/global_styles',
          webhooks: '/webhooks',
          marketplace: {
            tickets: {
              add: '/add',
              ':ticket': {},
            },
            purchase_page: '/purchase_page',
            custom_purchase_pages: {
              add: '/add',
              ':custom_purchase_pages': {
                edit_template: '/edit_template',
              },
            },
            thank_you_page: '/thank_you_page',
            upsell_page: '/upsell_page',
          },
          statistics: '/statistics',
          certificates: {
            create: '/create',
            ':certificate': {
              design: '/design',
            },
          },
        },
      },
      marketplace: {
        account: {
          connected: '/:stripeAccountId/connected',
          retry: '/:stripeAccountId/retry',
        },
        tickets: {},
      },
      add_ons: {
        purchase_add_on: '/purchase_add_on',
      },
      obie: {
        categories: {
          ':category': {
            profile: {
              ':profile': {
                blocks: {
                  ':block': {
                    process: '/process',
                    complete: '/complete',
                  },
                },
              },
            },
          },
        },
        pag: {
          ':pag': {
            profile: {
              ':profile': {
                day: {
                  ':day': {
                    session: {
                      ':session': {},
                    },
                  },
                },
              },
            },
          },
        },
        assistants: {
          ':assistant': {
            threads: {
              ':thread': {},
            },
            settings: '/settings',
          },
          create: '/create',
        },
      },
    },
    // Namespace
    `organization/${organization.id}`,
  )
}

function findOrganization(id: number | null) {
  const url = api(`/organizations/${id}`)
  return teamMemberClient.get<Organization>(url)
}
